/**
 * The single addressing rule both halves of the local cache obey.
 *
 * A media URL carries a per-session signature in its query, so the same
 * segment has a different URL for every viewer and every sitting. The
 * Worker's edge cache already solves this by keying immutable media on
 * the bare path; the local cache repeats that rule verbatim, so a
 * segment the player streamed and the same segment the prefetcher
 * walked to are one entry, never two.
 */

/**
 * Exactly the extensions the Worker serves as immutable. Playlists are
 * excluded by construction: they are rewritten per session and served
 * no-store, so a cached playlist would hand one viewer another
 * viewer's signatures.
 */
const CACHEABLE_EXTENSION = /\.(?:ts|opus|webp)$/;

const MEDIA_PREFIX = '/media/';

/** Asset ids as the ingest endpoint constrains them; never contain a colon. */
const ASSET_ID = /^[a-z0-9][a-z0-9-]{0,63}$/;

const DATA_CACHE_PREFIX = 'eternally-media:';
const GENERATION_CACHE_PREFIX = 'eternally-media-gen:';

export interface ParsedCacheName {
  kind: 'data' | 'generation';
  assetId: string;
  /** Present on generation markers only. */
  generation?: string;
}

export function isCacheableMediaPath(pathname: string): boolean {
  return pathname.startsWith(MEDIA_PREFIX) && CACHEABLE_EXTENSION.test(pathname);
}

export function assetIdFromMediaPath(pathname: string): string | null {
  if (!pathname.startsWith(MEDIA_PREFIX)) {
    return null;
  }
  const rest = pathname.slice(MEDIA_PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash <= 0) {
    return null;
  }
  const assetId = rest.slice(0, slash);
  return ASSET_ID.test(assetId) ? assetId : null;
}

/** Origin and path only: the signature query is never part of identity. */
export function canonicalKey(url: URL | string): string {
  const parsed = typeof url === 'string' ? new URL(url) : url;
  return `${parsed.origin}${parsed.pathname}`;
}

export function mediaCacheName(assetId: string): string {
  return `${DATA_CACHE_PREFIX}${assetId}`;
}

/**
 * A re-ingest keeps every path and changes every byte, so the local
 * copy must be versioned explicitly. The generation is recorded by the
 * existence of an empty marker cache rather than by an entry inside the
 * data cache, which keeps the data cache's key count equal to the
 * number of cached files and therefore directly usable as the readout.
 */
export function generationCacheName(assetId: string, generation: string): string {
  return `${GENERATION_CACHE_PREFIX}${assetId}:${encodeURIComponent(generation)}`;
}

export function parseCacheName(name: string): ParsedCacheName | null {
  if (name.startsWith(DATA_CACHE_PREFIX)) {
    const assetId = name.slice(DATA_CACHE_PREFIX.length);
    return ASSET_ID.test(assetId) ? { kind: 'data', assetId } : null;
  }
  if (name.startsWith(GENERATION_CACHE_PREFIX)) {
    const rest = name.slice(GENERATION_CACHE_PREFIX.length);
    const colon = rest.indexOf(':');
    if (colon <= 0) {
      return null;
    }
    const assetId = rest.slice(0, colon);
    if (!ASSET_ID.test(assetId)) {
      return null;
    }
    return {
      kind: 'generation',
      assetId,
      generation: decodeURIComponent(rest.slice(colon + 1)),
    };
  }
  return null;
}
